import React, { useState } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiBarChart3, FiDownload, FiCalendar, FiUsers, FiDollarSign, FiTrendingUp, FiFilter, FiRefreshCw } = FiIcons;

const UsageReports = () => {
    const [selectedReport, setSelectedReport] = useState('overview');
    const [dateRange, setDateRange] = useState('30days');
    const [isGenerating, setIsGenerating] = useState(false);

    const reportTypes = [
        { id: 'overview', name: 'Usage Overview', icon: FiBarChart3 },
        { id: 'users', name: 'User Activity', icon: FiUsers },
        { id: 'revenue', name: 'Revenue Analytics', icon: FiDollarSign },
        { id: 'features', name: 'Feature Usage', icon: FiTrendingUp }
    ];

    const dateRanges = [
        { id: '7days', name: 'Last 7 Days' },
        { id: '30days', name: 'Last 30 Days' },
        { id: '90days', name: 'Last 3 Months' },
        { id: '1year', name: 'Last Year' },
        { id: 'custom', name: 'Custom Range' }
    ];

    const overviewMetrics = [
        { title: 'Total Users', value: '2,847', change: '+12.5%', trend: 'up', color: 'blue' },
        { title: 'Active Users (30d)', value: '1,923', change: '+8.2%', trend: 'up', color: 'green' },
        { title: 'Monthly Revenue', value: '$15,230', change: '+15.3%', trend: 'up', color: 'purple' },
        { title: 'Churn Rate', value: '2.1%', change: '-0.3%', trend: 'down', color: 'orange' }
    ];

    const userActivityData = [
        { metric: 'Daily Active Users', value: '1,234', percentage: 67 },
        { metric: 'Weekly Active Users', value: '1,856', percentage: 85 },
        { metric: 'Monthly Active Users', value: '1,923', percentage: 92 },
        { metric: 'Session Duration (avg)', value: '24 min', percentage: 78 }
    ];

    const revenueBreakdown = [
        { plan: 'Free', users: 1205, revenue: 0, color: 'gray' },
        { plan: 'Pro', users: 1456, revenue: 11635, color: 'blue' },
        { plan: 'Team', users: 186, revenue: 5578, color: 'purple' }
    ];

    const featureUsage = [
        { feature: 'Expense Tracker', usage: 95, users: 2705 },
        { feature: 'Reports & Analytics', usage: 78, users: 2220 },
        { feature: 'Team Collaboration', usage: 45, users: 1281 },
        { feature: 'API Access', usage: 23, users: 655 },
        { feature: 'Mobile App', usage: 67, users: 1907 }
    ];

    const generateReport = async () => {
        setIsGenerating(true);
        // Simulate report generation
        setTimeout(() => {
            setIsGenerating(false);
            toast.success('Report generated successfully!');
        }, 2000);
    };

    const exportReport = (format) => {
        toast.success(`Exporting report as ${format.toUpperCase()}...`);
    };

    const renderOverviewReport = () => (
        <div className="space-y-6">
            {/* Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {overviewMetrics.map((metric, index) => (
                    <motion.div
                        key={metric.title}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${metric.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/20' :
                                    metric.color === 'green' ? 'bg-green-100 dark:bg-green-900/20' :
                                        metric.color === 'purple' ? 'bg-purple-100 dark:bg-purple-900/20' :
                                            'bg-orange-100 dark:bg-orange-900/20'
                                }`}>
                                <SafeIcon icon={FiBarChart3} className={`w-6 h-6 ${metric.color === 'blue' ? 'text-blue-600 dark:text-blue-400' :
                                        metric.color === 'green' ? 'text-green-600 dark:text-green-400' :
                                            metric.color === 'purple' ? 'text-purple-600 dark:text-purple-400' :
                                                'text-orange-600 dark:text-orange-400'
                                    }`} />
                            </div>
                            <span className={`text-sm font-medium ${metric.trend === 'up' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                                }`}>
                                {metric.change}
                            </span>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                            {metric.value}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 text-sm">
                            {metric.title}
                        </p>
                    </motion.div>
                ))}
            </div>

            {/* Chart Placeholder */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Usage Trends
                </h3>
                <div className="h-64 bg-gray-50 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                    <p className="text-gray-500 dark:text-gray-400">Chart visualization would go here</p>
                </div>
            </div>
        </div>
    );

    const renderUserActivityReport = () => (
        <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                    User Engagement Metrics
                </h3>
                <div className="space-y-4">
                    {userActivityData.map((item, index) => (
                        <div key={item.metric} className="flex items-center justify-between">
                            <div className="flex-1">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                                        {item.metric}
                                    </span>
                                    <span className="text-sm text-gray-600 dark:text-gray-300">
                                        {item.value}
                                    </span>
                                </div>
                                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                    <div
                                        className="bg-gradient-to-r from-primary-500 to-accent-500 h-2 rounded-full transition-all duration-300"
                                        style={{ width: `${item.percentage}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderRevenueReport = () => (
        <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                    Revenue by Plan
                </h3>
                <div className="space-y-4">
                    {revenueBreakdown.map((plan, index) => (
                        <div key={plan.plan} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <div className="flex items-center">
                                <div className={`w-4 h-4 rounded-full mr-3 ${plan.color === 'gray' ? 'bg-gray-400' :
                                        plan.color === 'blue' ? 'bg-blue-500' :
                                            'bg-purple-500'
                                    }`} />
                                <div>
                                    <p className="font-medium text-gray-900 dark:text-white">{plan.plan} Plan</p>
                                    <p className="text-sm text-gray-600 dark:text-gray-300">{plan.users} users</p>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="font-semibold text-gray-900 dark:text-white">
                                    ${plan.revenue.toLocaleString()}
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-300">
                                    ${(plan.revenue / plan.users || 0).toFixed(2)} per user
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderFeatureUsageReport = () => (
        <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                    Feature Adoption
                </h3>
                <div className="space-y-4">
                    {featureUsage.map((feature, index) => (
                        <div key={feature.feature} className="space-y-2">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {feature.feature}
                                </span>
                                <div className="text-right">
                                    <span className="text-sm text-gray-900 dark:text-white font-medium">
                                        {feature.usage}%
                                    </span>
                                    <p className="text-xs text-gray-600 dark:text-gray-300">
                                        {feature.users} users
                                    </p>
                                </div>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                <div
                                    className="bg-gradient-to-r from-primary-500 to-accent-500 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${feature.usage}%` }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderReportContent = () => {
        switch (selectedReport) {
            case 'overview': return renderOverviewReport();
            case 'users': return renderUserActivityReport();
            case 'revenue': return renderRevenueReport();
            case 'features': return renderFeatureUsageReport();
            default: return renderOverviewReport();
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="mb-8"
                >
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                        Usage Reports
                    </h1>
                    <p className="text-gray-600 dark:text-gray-300">
                        Analyze platform usage and generate detailed reports
                    </p>
                </motion.div>

                {/* Controls */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8"
                >
                    <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between">
                        <div className="flex flex-col sm:flex-row gap-4 flex-1">
                            {/* Report Type */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Report Type
                                </label>
                                <select
                                    value={selectedReport}
                                    onChange={(e) => setSelectedReport(e.target.value)}
                                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                >
                                    {reportTypes.map(type => (
                                        <option key={type.id} value={type.id}>{type.name}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Date Range */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Date Range
                                </label>
                                <select
                                    value={dateRange}
                                    onChange={(e) => setDateRange(e.target.value)}
                                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                >
                                    {dateRanges.map(range => (
                                        <option key={range.id} value={range.id}>{range.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={generateReport}
                                disabled={isGenerating}
                                className="flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                            >
                                <SafeIcon icon={isGenerating ? FiRefreshCw : FiFilter} className={`w-4 h-4 mr-2 ${isGenerating ? 'animate-spin' : ''}`} />
                                {isGenerating ? 'Generating...' : 'Generate'}
                            </button>

                            <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                                <button
                                    onClick={() => exportReport('excel')}
                                    className="px-3 py-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-sm"
                                >
                                    Excel
                                </button>
                                <button
                                    onClick={() => exportReport('pdf')}
                                    className="px-3 py-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-sm border-l border-gray-300 dark:border-gray-600"
                                >
                                    PDF
                                </button>
                                <button
                                    onClick={() => exportReport('image')}
                                    className="px-3 py-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-sm border-l border-gray-300 dark:border-gray-600"
                                >
                                    Image
                                </button>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Report Content */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    {renderReportContent()}
                </motion.div>
            </div>
        </div>
    );
};

export default UsageReports;